import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Library, Book, User, Lightbulb, Wrench, ArrowLeft, ExternalLink, Sparkles, TrendingUp, MessageSquarePlus, X, Send, Loader2 } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import BottomNav from '../components/BottomNav';

const TYPE_ICONS = {
  book: Book,
  teacher: User,
  course: Lightbulb,
  practice: Lightbulb,
  tool: Wrench,
};

export const TheINN = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [resources, setResources] = useState([]);
  const [filterType, setFilterType] = useState('all');
  const [recommendations, setRecommendations] = useState([]);
  const [focusAreas, setFocusAreas] = useState([]);
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [suggestionLink, setSuggestionLink] = useState('');
  const [suggestionNote, setSuggestionNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedDomain) {
      fetchResources(selectedDomain);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDomain]);

  const fetchData = async () => {
    try {
      // Fetch domains first (doesn't require auth)
      const domainsRes = await api.get('/inn/domains');
      setDomains(domainsRes.data.domains);
      
      if (domainsRes.data.domains.length > 0) {
        setSelectedDomain(domainsRes.data.domains[0].domain);
      }
      
      // Try to fetch recommendations (requires auth, may fail)
      try {
        const recsRes = await api.get('/inn/recommendations');
        setRecommendations(recsRes.data.recommendations || []);
        setFocusAreas(recsRes.data.focus_areas || []);
      } catch (recsError) {
        // Recommendations require auth - silently fail if not authenticated
        console.log('Recommendations not available (may require assessment)');
        setRecommendations([]);
        setFocusAreas([]);
      }
    } catch (error) {
      toast.error(t('inn.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchResources = async (domain) => {
    try {
      const response = await api.get(`/inn/resources?domain=${encodeURIComponent(domain)}`);
      setResources(response.data.resources);
    } catch (error) {
      toast.error(t('inn.resourcesFailed'));
    }
  };

  const filteredResources = filterType === 'all' 
    ? resources 
    : resources.filter(r => r.type === filterType);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[#D4AF37]">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative py-16 px-6 pb-32 lg:pb-16">
      <div className="max-w-7xl mx-auto">
        <button
          onClick={() => navigate('/dashboard')}
          className="text-white/70 hover:text-white transition-colors duration-300 uppercase tracking-widest text-sm font-sans mb-8 flex items-center gap-2"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('nav.dashboard')}
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <Library className="w-16 h-16 text-[#D4AF37] mx-auto mb-4" />
          <h1 className="font-serif font-light text-4xl text-[#F8F5F2] mb-4">
            {t('inn.title')}
          </h1>
          <p className="font-sans font-light text-[#D1D1D6] text-lg">
            {t('inn.subtitle')}
          </p>
        </motion.div>

        {/* Personalized Recommendations Section */}
        {recommendations.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-6">
              <Sparkles className="w-6 h-6 text-[#D4AF37]" />
              <h2 className="font-serif font-light text-2xl text-[#F8F5F2]">
                {language === 'tr' ? 'Sizin İçin Öneriler' : 'Recommended For You'}
              </h2>
            </div>

            {/* Focus Areas Pills */}
            <div className="flex flex-wrap gap-3 mb-6">
              {focusAreas.map((area, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-4 py-2"
                >
                  <TrendingUp className="w-4 h-4 text-[#D4AF37]" />
                  <span className="font-sans text-sm text-[#F8F5F2]">{area.domain}</span>
                  <span className="font-sans text-xs text-[#D4AF37] bg-[#D4AF37]/20 px-2 py-0.5 rounded-full">
                    {area.score}/10
                  </span>
                </div>
              ))}
            </div>

            {/* Recommended Resources Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {recommendations.slice(0, 6).map((resource, index) => {
                const Icon = TYPE_ICONS[resource.type] || Book;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + index * 0.05 }}
                    className="glass-active rounded-2xl p-6 border-[#D4AF37]/30 hover:border-[#D4AF37]/50 transition-all"
                    data-testid={`recommendation-${index}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-lg bg-[#D4AF37]/20">
                        <Icon className="w-6 h-6 text-[#D4AF37]" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="font-sans text-lg text-[#F8F5F2] mb-1">
                              {resource.title}
                            </h4>
                            {resource.author && (
                              <p className="font-sans text-sm text-[#D4AF37]">
                                {resource.author}
                              </p>
                            )}
                          </div>
                        </div>
                        <p className="font-sans text-sm text-[#D1D1D6] mb-3 leading-relaxed">
                          {resource.description}
                        </p>
                        <p className="font-sans text-xs text-[#D4AF37]/70 italic mb-3">
                          {resource.recommended_because}
                        </p>
                        {resource.link && (
                          <a
                            href={resource.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#F8F5F2] transition-colors font-sans text-sm"
                          >
                            {t('inn.learnMore')} <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Divider */}
        {recommendations.length > 0 && (
          <div className="border-t border-[#D4AF37]/20 my-12" />
        )}

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Domain Sidebar */}
          <div className="lg:col-span-3">
            <div className="glass-panel rounded-2xl p-4 sticky top-8">
              <h3 className="font-sans uppercase tracking-[0.2em] text-[#D4AF37] text-xs font-semibold mb-4">
                {t('inn.domains')}
              </h3>
              <div className="space-y-2">
                {domains.map((domain) => (
                  <button
                    key={domain.domain}
                    onClick={() => setSelectedDomain(domain.domain)}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-all font-sans text-sm ${
                      selectedDomain === domain.domain
                        ? 'bg-[#D4AF37]/20 text-[#F8F5F2] border border-[#D4AF37]/40'
                        : 'text-[#A1A1AA] hover:text-[#F8F5F2] hover:bg-white/5'
                    }`}
                    data-testid={`domain-${domain.domain}`}
                  >
                    <div className="flex justify-between items-center">
                      <span>{domain.domain}</span>
                      <span className="text-xs text-[#D4AF37]">{domain.count}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Resources */}
          <div className="lg:col-span-9">
            {/* Filter */}
            <div className="flex flex-wrap gap-2 mb-6">
              {['all', 'book', 'teacher', 'practice', 'course', 'tool'].map((type) => (
                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  className={`px-4 py-2 rounded-full border transition-all font-sans text-sm capitalize ${
                    filterType === type
                      ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]'
                      : 'border-[#D4AF37]/20 text-[#A1A1AA] hover:border-[#D4AF37]/40'
                  }`}
                  data-testid={`filter-${type}`}
                >
                  {t(`inn.${type}`)}
                </button>
              ))}
            </div>

            {/* Resource Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              {filteredResources.map((resource, index) => {
                const Icon = TYPE_ICONS[resource.type] || Book;
                return (
                  <motion.div
                    key={resource.id || index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="glass-panel rounded-2xl p-6 hover:border-[#D4AF37]/30 transition-all"
                    data-testid={`resource-${index}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-lg bg-[#D4AF37]/10">
                        <Icon className="w-6 h-6 text-[#D4AF37]" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="font-sans text-lg text-[#F8F5F2] mb-1">
                              {resource.title}
                            </h4>
                            {resource.author && (
                              <p className="font-sans text-sm text-[#D4AF37]">
                                {resource.author}
                              </p>
                            )}
                          </div>
                          <span className="text-xs uppercase tracking-wider bg-[#D4AF37]/10 text-[#D4AF37] px-2 py-1 rounded-full font-sans">
                            {resource.type}
                          </span>
                        </div>
                        <p className="font-sans text-sm text-[#D1D1D6] mb-4 leading-relaxed">
                          {resource.description}
                        </p>
                        {resource.link && (
                          <a
                            href={resource.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#F8F5F2] transition-colors font-sans text-sm"
                          >
                            {t('inn.learnMore')} <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {filteredResources.length === 0 && (
              <div className="text-center py-12">
                <p className="font-sans text-[#A1A1AA]">
                  {t('inn.noResources')}
                </p>
              </div>
            )}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-12 text-center glass-panel rounded-2xl p-8"
        >
          <p className="font-sans text-[#D1D1D6] mb-4">
            {t('inn.growingLibrary')}
          </p>
          <p className="font-sans text-sm text-[#A1A1AA] mb-6">
            {t('inn.contribute')}
          </p>
          <button
            onClick={() => setShowSuggestModal(true)}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#030305] transition-all duration-300 font-sans text-sm"
            data-testid="suggest-resource-btn"
          >
            <MessageSquarePlus className="w-4 h-4" />
            {language === 'tr' ? 'Kaynak Öner' : 'Suggest a Resource'}
          </button>
        </motion.div>
      </div>

      {/* Suggestion Modal */}
      <AnimatePresence>
        {showSuggestModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowSuggestModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-[#0a0a0f] border border-[#D4AF37]/30 rounded-2xl p-8 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-serif text-2xl text-[#F8F5F2]">
                  {language === 'tr' ? 'Kaynak Öner' : 'Suggest a Resource'}
                </h3>
                <button
                  onClick={() => setShowSuggestModal(false)}
                  className="text-[#A1A1AA] hover:text-[#F8F5F2] transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={async (e) => {
                e.preventDefault();
                if (!suggestionLink.trim()) {
                  toast.error(language === 'tr' ? 'Lütfen bir link girin' : 'Please enter a link');
                  return;
                }
                setSubmitting(true);
                try {
                  await api.post('/inn/suggest', { 
                    link: suggestionLink.trim(),
                    note: suggestionNote.trim()
                  });
                  toast.success(language === 'tr' ? 'Öneriniz için teşekkürler!' : 'Thank you for your suggestion!');
                  setSuggestionLink('');
                  setSuggestionNote('');
                  setShowSuggestModal(false);
                } catch (error) {
                  toast.error(language === 'tr' ? 'Öneri gönderilemedi' : 'Failed to submit suggestion');
                } finally {
                  setSubmitting(false);
                }
              }}>
                <div className="space-y-4">
                  <div>
                    <label className="block font-sans text-sm text-[#A1A1AA] mb-2">
                      {language === 'tr' ? 'Link *' : 'Link *'}
                    </label>
                    <input
                      type="url"
                      value={suggestionLink}
                      onChange={(e) => setSuggestionLink(e.target.value)}
                      placeholder={language === 'tr' ? 'https://...' : 'https://...'}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-[#F8F5F2] placeholder-[#6B7280] focus:border-[#D4AF37]/50 focus:outline-none transition-colors font-sans"
                      required
                      data-testid="suggestion-link-input"
                    />
                  </div>
                  <div>
                    <label className="block font-sans text-sm text-[#A1A1AA] mb-2">
                      {language === 'tr' ? 'Not (isteğe bağlı)' : 'Note (optional)'}
                    </label>
                    <textarea
                      value={suggestionNote}
                      onChange={(e) => setSuggestionNote(e.target.value)}
                      placeholder={language === 'tr' ? 'Bu kaynağı neden öneriyorsunuz?' : 'Why do you recommend this resource?'}
                      rows={3}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-[#F8F5F2] placeholder-[#6B7280] focus:border-[#D4AF37]/50 focus:outline-none transition-colors font-sans resize-none"
                      data-testid="suggestion-note-input"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full mt-6 flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#D4AF37] text-[#030305] hover:bg-[#D4AF37]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 font-sans font-semibold"
                  data-testid="submit-suggestion-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {language === 'tr' ? 'Gönderiliyor...' : 'Submitting...'}
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      {language === 'tr' ? 'Gönder' : 'Submit'}
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <BottomNav />
    </div>
  );
};

export default TheINN;
